import * as React from 'react';
import { makeStyles, tokens, Text, Spinner } from '@fluentui/react-components';
import { useIntl } from 'react-intl';
import { usePresentationSync } from '../sync/PresentationSyncContext';
import { useWorkspaceResolver } from '../sync/WorkspaceResolverContext';
import { syncStatusMessages } from '../intl/messages';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke1,
    backgroundColor: tokens.colorNeutralBackground2,
    boxShadow: tokens.shadow4,
  },
  info: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
    flex: 1,
  },
  text: {
    color: tokens.colorNeutralForeground2,
  },
  hint: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
});

export function SyncStatusBar() {
  const styles = useStyles();
  const intl = useIntl();
  const { resolvedFile } = usePresentationSync();
  const { status } = useWorkspaceResolver();

  const renderContent = () => {
    if (status === 'idle' || status === 'resolving') {
      return (
        <Spinner
          size="extra-tiny"
          label={intl.formatMessage(syncStatusMessages.resolving)}
          labelPosition="after"
        />
      );
    }

    if (status === 'resolved' && resolvedFile) {
      return (
        <>
          <Text className={styles.text}>
            {resolvedFile.meta?.title || resolvedFile.name}
          </Text>
          <Text className={styles.text}>
            {intl.formatMessage(syncStatusMessages.synced)}
          </Text>
        </>
      );
    }

    if (status === 'multiple-in-workspace') {
      return (
        <>
          <Text className={styles.text}>
            {intl.formatMessage(syncStatusMessages.multipleInWorkspaceTitle)}
          </Text>
          <Text className={styles.hint}>
            {intl.formatMessage(syncStatusMessages.multipleInWorkspaceHint)}
          </Text>
        </>
      );
    }

    // not-synced (or resolved with no file — shouldn't happen, but safe fallback)
    return (
      <>
        <Text className={styles.text}>
          {intl.formatMessage(syncStatusMessages.notSyncedTitle)}
        </Text>
        <Text className={styles.hint}>
          {intl.formatMessage(syncStatusMessages.notSyncedHint)}
        </Text>
      </>
    );
  };

  return (
    <div className={styles.root}>
      <div className={styles.info} role="status" aria-live="polite">
        {renderContent()}
      </div>
    </div>
  );
}
